import {
  Camera,
  FileText,
  GearSix,
  ShieldCheck,
  SignOut,
} from '@phosphor-icons/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { ErrorState } from '@/components/error-state'
import { LoadingState } from '@/components/loading-state'
import { ApiError } from '@/lib/api/errors'
import { friendlyErrorKey } from '@/lib/api/friendly-errors'
import { formatNumber } from '@/lib/i18n/format'
import { KuniriIcons, kuniriIconWeight } from '@/lib/icons'
import { useUiStore } from '@/lib/ui/ui-store'
import { logoutAccount, syncLocaleFromProfile } from '@/features/auth/session'

import {
  profileSocialApi,
  type FriendsResponse,
  type PrivateProfile,
  type UpdateProfileRequest,
} from '../api'
import {
  prepareAvatarFile,
  validateAvatarFile,
} from '../avatar'

import { EditProfileSheet } from '../components/edit-profile-sheet'
import { ProfileMetrics } from '../components/profile-metrics'
import { PullToRefresh } from '../components/pull-to-refresh'
import { RefreshButton } from '../components/refresh-button'
import { UserAvatar } from '../components/user-avatar'
import { useIsDesktop } from '../hooks'

type ProfileState =
  | { status: 'loading' }
  | { status: 'ready'; profile: PrivateProfile }
  | { status: 'error'; error: unknown }

interface NoticeState {
  tone: 'success' | 'error' | 'info'
  key: string
  values?: Record<string, string | number>
}

function displayName(profile: PrivateProfile): string {
  const fullName = [profile.firstName, profile.lastName]
    .map((part) => part.trim())
    .filter(Boolean)
    .join(' ')
  return fullName || profile.publicName
}

function ProfileNotice({ notice }: { notice: NoticeState }) {
  const { t } = useTranslation()
  return (
    <div
      role={notice.tone === 'error' ? 'alert' : 'status'}
      className={`rounded-md px-4 py-3 text-sm font-semibold ${
        notice.tone === 'error'
          ? 'bg-[var(--danger-soft)] text-danger'
          : 'bg-mint text-teal'
      }`}
    >
      {t(notice.key, notice.values)}
    </div>
  )
}

/**
 * `/profile` (UI_PARITY §3.16, D-043/D-053): own-profile card, stats, avatar
 * upload, edit sheet with city/locale, friends entry with pending badge,
 * notification preferences entry, legal links, admin-only dashboard and the
 * account lifecycle actions.
 */
export function ProfileScreen() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const isDesktop = useIsDesktop()
  const pendingActivityPath = useUiStore((state) => state.pendingActivityPath)
  const locale = i18n.language === 'en' ? 'en' : 'it'

  const [state, setState] = useState<ProfileState>({ status: 'loading' })
  const [friends, setFriends] = useState<FriendsResponse | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [notice, setNotice] = useState<NoticeState | null>(null)
  const [editOpen, setEditOpen] = useState(false)
  const [editBusy, setEditBusy] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)
  const [logoutBusy, setLogoutBusy] = useState(false)
  const [avatarBusy, setAvatarBusy] = useState(false)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const avatarInputRef = useRef<HTMLInputElement>(null)
  const hasProfileRef = useRef(false)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    const controller = new AbortController()

    void profileSocialApi
      .getMe({ signal: controller.signal })
      .then((profile) => {
        if (controller.signal.aborted) return
        hasProfileRef.current = true
        setState({ status: 'ready', profile })
        return profileSocialApi
          .listFriends({ signal: controller.signal })
          .then((response) => {
            if (!controller.signal.aborted) setFriends(response)
          })
          .catch(() => {
            if (!controller.signal.aborted) setFriends(null)
          })
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return
        if (hasProfileRef.current) {
          setNotice({ tone: 'error', key: friendlyErrorKey(error) })
        } else {
          setState({ status: 'error', error })
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setRefreshing(false)
      })

    return () => controller.abort()
  }, [reloadKey])

  const handleRefresh = useCallback(() => {
    setRefreshing(true)
    setReloadKey((key) => key + 1)
  }, [])

  const retry = useCallback(() => {
    setState({ status: 'loading' })
    setReloadKey((key) => key + 1)
  }, [])

  const applySubset = useCallback(
    (subset: Awaited<ReturnType<typeof profileSocialApi.updateMe>>) => {
      setState((current) =>
        current.status === 'ready'
          ? { status: 'ready', profile: { ...current.profile, ...subset } }
          : current,
      )
    },
    [],
  )

  const handleSave = useCallback(
    async (body: UpdateProfileRequest) => {
      setEditBusy(true)
      setEditError(null)
      setNotice(null)
      try {
        const subset = await profileSocialApi.updateMe(body)
        applySubset(subset)
        await syncLocaleFromProfile(subset.locale ?? locale)
        setEditOpen(false)
        setNotice({ tone: 'success', key: 'profileSocial.profile.saved' })
      } catch (error) {
        setEditError(
          error instanceof ApiError && error.status === 409
            ? 'profileSocial.profile.editErrors.nicknameTaken'
            : friendlyErrorKey(error),
        )
      } finally {
        setEditBusy(false)
      }
    },
    [applySubset, locale],
  )

  const handleAvatarChange = useCallback(
    async (file: File | undefined) => {
      if (!file) return
      const validationKey = validateAvatarFile(file)
      if (validationKey) {
        setNotice({ tone: 'error', key: validationKey })
        return
      }
      setAvatarBusy(true)
      setNotice(null)
      const preview =
        typeof URL.createObjectURL === 'function'
          ? URL.createObjectURL(file)
          : null
      setAvatarPreview(preview)
      try {
        const prepared = await prepareAvatarFile(file)
        const subset = await profileSocialApi.uploadAvatar(prepared)
        applySubset(subset)
        setNotice({
          tone: 'success',
          key: 'profileSocial.profile.avatarUpdated',
        })
      } catch (error) {
        setNotice({ tone: 'error', key: friendlyErrorKey(error) })
      } finally {
        setAvatarBusy(false)
        setAvatarPreview(null)
        if (preview) URL.revokeObjectURL(preview)
      }
    },
    [applySubset],
  )

  const handleLogout = useCallback(async () => {
    setLogoutBusy(true)
    await logoutAccount()
    navigate('/onboarding', { replace: true })
  }, [navigate])

  const profile = state.status === 'ready' ? state.profile : null
  const incomingCount = friends?.incoming.length ?? 0
  const friendsLabel =
    incomingCount > 0
      ? t('profileSocial.profile.friendsLinkPending', {
          count: incomingCount,
        })
      : t('profileSocial.profile.friendsLink')

  return (
    <section aria-labelledby="profile-title" className="content-width pb-8">
      <div className="mx-auto w-full max-w-[820px]">
        <div className="flex items-center justify-end gap-3 pt-2 pb-3">
          <h1 id="profile-title" className="sr-only">
            {t('screens.profile.title')}
          </h1>
        </div>

        {notice ? (
          <div className="mb-4">
            <ProfileNotice notice={notice} />
          </div>
        ) : null}

        {state.status === 'loading' ? (
          <LoadingState labelKey="profileSocial.profile.loading" />
        ) : state.status === 'error' ? (
          <ErrorState
            error={state.error}
            titleKey="profileSocial.profile.loadErrorTitle"
            bodyKey="profileSocial.profile.loadErrorBody"
            onRetry={retry}
            retryLabelKey="profileSocial.profile.retry"
          />
        ) : profile ? (
          <PullToRefresh
            onRefresh={handleRefresh}
            refreshing={refreshing}
            disabled={isDesktop}
          >
            <div className="space-y-3">
              <div
                data-testid="profile-main-card"
                className="relative rounded-[28px] border border-line bg-white p-4 shadow-card sm:p-5"
              >
                {isDesktop ? (
                  <div className="absolute top-4 right-4 z-10 sm:top-5 sm:right-5">
                    <RefreshButton
                      onRefresh={handleRefresh}
                      refreshing={refreshing}
                      labelKey="profileSocial.profile.refresh"
                    />
                  </div>
                ) : null}
                <div className="flex flex-col items-center text-center">
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="sr-only"
                    tabIndex={-1}
                    aria-hidden="true"
                    data-testid="profile-avatar-input"
                    onChange={(event) => {
                      const file = event.target.files?.[0]
                      event.target.value = ''
                      void handleAvatarChange(file)
                    }}
                  />
                  <button
                    type="button"
                    className="relative rounded-full"
                    aria-label={
                      profile.avatarUrl
                        ? t('profileSocial.profile.changePhoto')
                        : t('profileSocial.profile.choosePhoto')
                    }
                    aria-busy={avatarBusy}
                    disabled={avatarBusy}
                    onClick={() => avatarInputRef.current?.click()}
                  >
                    <UserAvatar
                      name={displayName(profile)}
                      src={avatarPreview ?? profile.avatarUrl}
                      size="profile"
                    />
                    <span
                      className="absolute right-0 -bottom-1 grid size-8 place-items-center rounded-full border border-line bg-white text-teal"
                      aria-hidden="true"
                    >
                      <Camera size={16} weight={kuniriIconWeight} />
                    </span>
                  </button>
                  {avatarBusy ? (
                    <p className="mt-2 text-xs text-muted" aria-live="polite">
                      {t('profileSocial.profile.uploadingAvatar')}
                    </p>
                  ) : null}

                  <p className="mt-3 font-body text-[26px] leading-[1.05] font-bold text-deep">
                    {displayName(profile)}
                  </p>
                  <p className="text-sm break-all text-muted">{profile.email}</p>
                  {pendingActivityPath ? (
                    <Link
                      to={pendingActivityPath}
                      className="btn-ghost mt-1 px-2 text-sm"
                    >
                      <KuniriIcons.envelope
                        size={17}
                        weight={kuniriIconWeight}
                        aria-hidden="true"
                      />
                      {t('profileSocial.profile.pendingActivity')}
                    </Link>
                  ) : null}
                  <p className="mt-1 text-sm font-bold text-teal">
                    {t('profileSocial.profile.nickname', {
                      nickname: profile.nickname,
                    })}
                  </p>
                  {profile.publicName !== displayName(profile) ? (
                    <p className="text-sm text-muted">
                      {t('profileSocial.profile.publicNameLabel', {
                        name: profile.publicName,
                      })}
                    </p>
                  ) : null}
                  {profile.age != null ? (
                    <p className="text-sm text-muted">
                      {t('profileSocial.profile.ageYears', {
                        count: profile.age,
                      })}
                    </p>
                  ) : null}

                  <div className="mt-4 w-full text-left">
                    <ProfileMetrics
                      rating={profile.rating}
                      ratingCount={profile.ratingCount ?? 0}
                      activitiesCreated={profile.activitiesCreated ?? 0}
                      activitiesJoined={profile.activitiesJoined ?? 0}
                      activitiesParticipated={profile.activitiesParticipated ?? 0}
                    />
                  </div>

                  <Link
                    to="/friends"
                    className="btn-secondary mt-3 w-full bg-white"
                    aria-label={friendsLabel}
                  >
                    <KuniriIcons.users
                      size={20}
                      weight={kuniriIconWeight}
                      aria-hidden="true"
                    />
                    {t('profileSocial.profile.friendsLink')}
                    {incomingCount > 0 ? (
                      <span
                        className="ml-1 grid min-w-6 place-items-center rounded-full bg-coral px-1.5 py-0.5 text-xs font-bold text-deep"
                        aria-hidden="true"
                      >
                        {formatNumber(incomingCount, locale)}
                      </span>
                    ) : null}
                  </Link>

                  {profile.isAdmin ? (
                    <Link
                      to="/admin-dashboard"
                      className="btn-secondary mt-2 w-full bg-mint"
                    >
                      <ShieldCheck
                        size={20}
                        weight={kuniriIconWeight}
                        aria-hidden="true"
                      />
                      {t('profileSocial.profile.adminLink')}
                    </Link>
                  ) : null}
                </div>
              </div>

              {!profile.avatarUrl ? (
                <div className="rounded-[18px] border border-line bg-mint p-4">
                  <div className="flex items-start gap-3">
                    <span
                      className="grid size-9 shrink-0 place-items-center rounded-full bg-white text-teal"
                      aria-hidden="true"
                    >
                      <KuniriIcons.cameraPlus
                        size={18}
                        weight={kuniriIconWeight}
                      />
                    </span>
                    <div>
                      <p className="font-bold text-deep">
                        {t('profileSocial.profile.missingAvatarTitle')}
                      </p>
                      <p className="mt-1 text-sm leading-relaxed text-muted">
                        {t('profileSocial.profile.missingAvatarBody')}
                      </p>
                      <button
                        type="button"
                        className="btn-ghost mt-1 px-0 text-sm text-teal"
                        disabled={avatarBusy}
                        onClick={() => avatarInputRef.current?.click()}
                      >
                        {t('profileSocial.profile.choosePhoto')}
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}

              <dl className="space-y-2.5">
                {[
                  {
                    key: 'city',
                    icon: KuniriIcons.mapPin,
                    label: t('profileSocial.profile.sections.city'),
                    value: profile.city,
                  },
                  {
                    key: 'bio',
                    icon: KuniriIcons.sparkle,
                    label: t('profileSocial.profile.sections.bio'),
                    value: profile.bio,
                  },
                  {
                    key: 'languages',
                    icon: KuniriIcons.chat,
                    label: t('profileSocial.profile.sections.languages'),
                    value: profile.languages.join(' · '),
                    desktopOnly: true,
                  },
                  {
                    key: 'interests',
                    icon: KuniriIcons.heart,
                    label: t('profileSocial.profile.sections.interests'),
                    value: profile.interests.join(' · '),
                    desktopOnly: true,
                  },
                ].map((item) => {
                  const ItemIcon = item.icon
                  return (
                    <div
                      key={item.key}
                      className={`flex items-start gap-3 rounded-[18px] border border-line bg-mint p-3.5 ${
                        item.desktopOnly ? 'hidden min-[768px]:flex' : ''
                      }`}
                    >
                      <span
                        className="grid size-9 shrink-0 place-items-center rounded-[13px] bg-white text-teal"
                        aria-hidden="true"
                      >
                        <ItemIcon size={18} weight={kuniriIconWeight} />
                      </span>
                      <div className="min-w-0">
                        <dt className="font-bold text-deep">
                          {item.label}
                        </dt>
                        <dd className="mt-0.5 break-words text-sm leading-relaxed text-muted">
                          {item.value?.trim()
                            ? item.value
                            : t('profileSocial.profile.emptyValue')}
                        </dd>
                      </div>
                    </div>
                  )
                })}
              </dl>

              {/* D-099: configuration lives in the settings menu; the
                  profile card stays a read-only showcase. Full-width row like
                  the other profile entries. */}
              <Link
                  to="/settings"
                  className="btn-secondary w-full bg-white"
                  data-testid="profile-settings-link"
                >
                  <GearSix
                    size={20}
                    weight={kuniriIconWeight}
                    aria-hidden="true"
                  />
                  {t('screens.settings.title')}
                </Link>

              <div className="space-y-2 rounded-card border border-line bg-white p-4">
                <button
                  type="button"
                  className="btn-secondary w-full bg-white"
                  disabled={logoutBusy}
                  aria-busy={logoutBusy}
                  onClick={() => void handleLogout()}
                >
                  <SignOut
                    size={20}
                    weight={kuniriIconWeight}
                    aria-hidden="true"
                  />
                  {logoutBusy
                    ? t('profileSocial.profile.loggingOut')
                    : t('profileSocial.profile.logout')}
                </button>
                {/* D-099: account deletion lives under settings/account. */}
                <div className="space-y-1 border-t border-line pt-2">
                  <Link
                    to="/terms"
                    className="btn-ghost min-h-11 w-full justify-start px-2 text-sm"
                  >
                    <FileText
                      size={19}
                      weight={kuniriIconWeight}
                      aria-hidden="true"
                    />
                    {t('profileSocial.profile.legal.terms')}
                  </Link>
                  <Link
                    to="/privacy"
                    className="btn-ghost min-h-11 w-full justify-start px-2 text-sm"
                  >
                    <ShieldCheck
                      size={19}
                      weight={kuniriIconWeight}
                      aria-hidden="true"
                    />
                    {t('profileSocial.profile.legal.privacy')}
                  </Link>
                </div>
              </div>
            </div>
          </PullToRefresh>
        ) : null}
      </div>

      {editOpen && profile ? (
        <EditProfileSheet
          profile={profile}
          busy={editBusy}
          errorKey={editError}
          onSave={(body) => void handleSave(body)}
          onClose={() => setEditOpen(false)}
        />
      ) : null}

    </section>
  )
}
